import { AbstractCommand } from '../AbstractCommand';

import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { VideoEditor } from '@src/editor/VideoEditor';

export class UpdateKaraokeConfigCommand extends AbstractCommand {
  getName(): string {
    return 'UpdateKaraokeConfigCommand';
  }

  private oldKaraokeConfig: KaraokeConfig | null = null;
  private karaokeConfig: KaraokeConfig;
  private _shouldCreateUndoRedoEntry: boolean;

  constructor(
    editor: VideoEditor,
    karaokeConfig: KaraokeConfig,
    createUndoRedoEntry: boolean = true,
  ) {
    super(editor);
    this.karaokeConfig = karaokeConfig;
    this._shouldCreateUndoRedoEntry = createUndoRedoEntry;
  }

  shouldCreateUndoRedoEntry(): boolean {
    return this._shouldCreateUndoRedoEntry;
  }

  execute(): boolean {
    const currentConfig = this.editor
      .getKaraokeConfigsMap()
      .get(this.karaokeConfig.id);
    if (!currentConfig) {
      console.error('Cannot update karaoke config: Config not found');
      return false;
    }

    this.oldKaraokeConfig = { ...currentConfig };
    this.applyConfig(this.karaokeConfig);
    return true;
  }

  // Writes the config to the map AND to every clip that shares it. Undo goes
  // through here too: restoring only the map left the clips (what the renderer
  // draws) on the undone config.
  private applyConfig(config: KaraokeConfig): void {
    this.editor.updateKaraokeConfig(config);
    this.editor.getKaraokeTrack()?.karaokeClips.forEach(clip => {
      if (clip.karaokeConfig.id === config.id) {
        clip.setKaraokeConfig(config);
      }
    });

    // Mark editor as dirty
    this.editor.markDirty('karaoke_config_updated');
  }

  undo(): boolean {
    if (!this.oldKaraokeConfig) {
      console.error(
        'Cannot undo update karaoke config: No previous config stored',
      );
      return false;
    }
    this.applyConfig(this.oldKaraokeConfig);
    return true;
  }

  getDescription(): string {
    return 'Update karaoke configuration';
  }
}
